import { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../../utils/api';
import Modal from '../../components/Modal';
import Badge from '../../components/Badge';
import { formatDate } from '../../utils/dateHelpers';

const TYPE_COLORS = {
  Lecture: '#6B8063',
  Event: '#3498DB',
  Workshop: '#D4A017',
};

const TYPE_BADGE = { Lecture: 'valid', Event: 'planned', Workshop: 'expiring' };
const ACTIVITY_TYPES = ['Lecture', 'Event', 'Workshop'];

export default function GrandRoundsCalendar() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [addDate, setAddDate] = useState('');

  const fetchItems = async () => {
    try {
      const data = await api.get('/grand-rounds');
      setItems(data.grand_rounds || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const events = items.map((gr) => ({
    id: String(gr.id),
    title: `[${gr.activity_type}] ${gr.topic_name}`,
    start: gr.date,
    backgroundColor: TYPE_COLORS[gr.activity_type] || '#6B8063',
    borderColor: TYPE_COLORS[gr.activity_type] || '#6B8063',
    extendedProps: gr,
  }));

  const handleAdd = async (formData) => {
    try {
      await api.post('/grand-rounds', formData);
      setAddModal(false);
      fetchItems();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="text-sm font-medium" style={{ marginRight: '8px' }}>Legend:</span>
          {Object.entries(TYPE_COLORS).map(([name, color]) => (
            <span key={name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: color, display: 'inline-block' }} />
              {name}
            </span>
          ))}
        </div>
      </div>

      <div className="card">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
          events={events}
          eventClick={(info) => setSelected(info.event.extendedProps)}
          dateClick={(info) => { setAddDate(info.dateStr); setAddModal(true); }}
          height="auto"
          eventDisplay="block"
        />
      </div>

      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="Grand Round Details">
        {selected && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><label className="text-xs text-muted">Type</label><p><Badge status={TYPE_BADGE[selected.activity_type]} text={selected.activity_type} /></p></div>
            <div><label className="text-xs text-muted">Date</label><p>{formatDate(selected.date)}</p></div>
            <div style={{ gridColumn: '1 / -1' }}><label className="text-xs text-muted">Topic</label><p className="font-semibold">{selected.topic_name}</p></div>
            <div><label className="text-xs text-muted">Presenter</label><p>{selected.presenter || 'TBD'}</p></div>
            <div><label className="text-xs text-muted">Attendees</label><p className="font-semibold">{selected.attendee_count || 0}</p></div>
          </div>
        )}
      </Modal>

      <Modal isOpen={addModal} onClose={() => setAddModal(false)} title="Add Grand Round">
        <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.target); handleAdd(Object.fromEntries(fd)); }}>
          <div className="form-row">
            <div className="form-group">
              <label>Type *</label>
              <select name="activity_type" defaultValue="Lecture">
                {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Date *</label><input name="date" type="date" defaultValue={addDate} required /></div>
          </div>
          <div className="form-group"><label>Topic *</label><input name="topic_name" required /></div>
          <div className="form-group"><label>Presenter</label><input name="presenter" /></div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setAddModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
